import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { axiosInstance, LiveDataResponse, UserResponse } from "api";
import {
  SELECTED_PLANT_LOCAL_STORAGE_KEY,
  TOKENS_LOCAL_STORAGE_KEY,
} from "store/consts";
import { getFromLocalStorage, setToLocalStorage } from "utils/localStorage";

import { AppState } from "./types";

const initialState: AppState = {
  powerPlants: [],
  selectedPowerPlant: undefined,
  showLiveData: false,
  user: undefined,
  tokens: undefined,
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setLiveData(
      state,
      action: PayloadAction<LiveDataResponse & { offline?: boolean }>,
    ) {
      const powerPlant = state.powerPlants.find(
        (powerPlant) => powerPlant.meterId === action.payload.meterId,
      );

      if (powerPlant) powerPlant.liveData = action.payload.liveData;
      if (state.selectedPowerPlant?.meterId === action.payload.meterId) {
        state.selectedPowerPlant.liveData = action.payload.liveData;
        state.selectedPowerPlant.offline = action.payload.offline || false;
        if (action.payload.connectivityState !== undefined) {
          state.selectedPowerPlant.connectivityState = action.payload.connectivityState;
          state.selectedPowerPlant.connectivitySince = action.payload.connectivitySince ?? null;
          state.selectedPowerPlant.lastContactAt = action.payload.lastContactAt ?? null;
        }
      }
    },
    setShowLiveData(state, action: PayloadAction<boolean>) {
      state.showLiveData = action.payload;
    },
    setSelectedPowerPlant(
      state,
      action: PayloadAction<AppState["selectedPowerPlant"]>,
    ) {
      state.selectedPowerPlant = action.payload;
      setToLocalStorage(
        SELECTED_PLANT_LOCAL_STORAGE_KEY,
        action.payload?.meterId,
      );
    },
    setUser(state, action: PayloadAction<AppState["user"]>) {
      state.user = action.payload;
    },
    setTokens(state, action: PayloadAction<AppState["tokens"]>) {
      state.tokens = action.payload;
      setToLocalStorage(TOKENS_LOCAL_STORAGE_KEY, action.payload);
      axiosInstance.defaults.headers["Authorization"] =
        `Bearer ${action.payload?.accessToken}`;
    },
    setUserData(state, action: PayloadAction<UserResponse>) {
      state.user = action.payload.user;
      state.powerPlants = action.payload.powerPlants;
      // Preserve the user's selection across refreshes. Priority:
      //   1. Whatever they currently have selected (if it's still in the list)
      //   2. The plant remembered from a previous session in localStorage
      //   3. The first plant — only as a true cold-start fallback
      const savedMeterId = getFromLocalStorage<string>(
        SELECTED_PLANT_LOCAL_STORAGE_KEY,
      );
      const next =
        state.powerPlants.find(
          (p) => p.meterId === state.selectedPowerPlant?.meterId,
        ) ||
        state.powerPlants.find((p) => p.meterId === savedMeterId) ||
        state.powerPlants[0];
      state.selectedPowerPlant = next ? { ...next, offline: false } : undefined;
      setToLocalStorage(SELECTED_PLANT_LOCAL_STORAGE_KEY, next?.meterId);
    },
  },
});

export const {
  setLiveData,
  setShowLiveData,
  setSelectedPowerPlant,
  setUser,
  setTokens,
  setUserData,
} = appSlice.actions;
export default appSlice.reducer;
